import axios from 'axios';
import { devLog, devError, sanitizeForLog, prodLog } from './logger';

/**
 * Obtient l'URL de base de l'API
 * @returns {string} URL de base de l'API
 */
const getApiUrl = (endpoint) => {
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://api.alturionlabs.com' 
    : 'http://localhost:5000';
  return `${baseUrl}${endpoint}`;
};

// Mapping des produits vers leurs fichiers de téléchargement
const PRODUCT_DOWNLOADS = {
  'VWAP Avancé': {
    downloadLink: 'AlturionVwapPremium.zip',
    fileName: 'AlturionVwapPremium.zip'
  },
  'VWAP Premium': {
    downloadLink: 'AlturionVwapPremium.zip',
    fileName: 'AlturionVwapPremium.zip'
  },
  'VWAP Lite': {
    downloadLink: 'AlturionVwapLite.zip',
    fileName: 'AlturionVwapLite.zip'
  },
  'Trading Sessions': {
    downloadLink: 'AlturionTradingSessions.zip',
    fileName: 'AlturionTradingSessions.zip'
  },
  'Asian Session Levels': {
    downloadLink: 'AlturionAsianSession.zip',
    fileName: 'AlturionAsianSession.zip'
  },
  'Volume Profile': {
    downloadLink: 'AlturionVolumeProfilePremium.zip',
    fileName: 'AlturionVolumeProfilePremium.zip'
  },
  'Volume Profile Premium': {
    downloadLink: 'AlturionVolumeProfilePremium.zip',
    fileName: 'AlturionVolumeProfilePremium.zip'
  },
  'Volume Profile Lite': {
    downloadLink: 'AlturionVolumeProfileLite.zip',
    fileName: 'AlturionVolumeProfileLite.zip'
  },
  'Enhanced Session Levels': {
    downloadLink: 'AlturionESL.zip',
    fileName: 'AlturionESL.zip'
  },
  'Risk/Reward': {
    downloadLink: 'AlturionRiskRewardTool.zip',
    fileName: 'AlturionRiskRewardTool.zip'
  },
  'Alturion Open Levels': {
    downloadLink: 'AlturionOpenLevels.zip',
    fileName: 'AlturionOpenLevels.zip'
  },
  'Alturion Open Range Breakout': {
    downloadLink: 'AlturionORB.zip',
    fileName: 'AlturionORB.zip'
  },
  'Bar Timer': {
    downloadLink: 'AlturionBarTimer.zip',
    fileName: 'AlturionBarTimer.zip'
  },
  'NYPA': {
    downloadLink: 'AlturionNYPA.zip',
    fileName: 'AlturionNYPA.zip'
  },
  'Economic Calendar': {
    downloadLink: 'AlturionEconomicCalendar.zip',
    fileName: 'AlturionEconomicCalendar.zip'
  },
  'Account Locker': {
    downloadLink: 'AlturionAccountLocker.zip',
    fileName: 'AlturionAccountLocker.zip'
  },
  'Footprint': {
    downloadLink: 'AlturionFootprintPro.zip',
    fileName: 'AlturionFootprintPro.zip'
  },
  'Tape Power Meter': {
    downloadLink: 'AlturionTapePowerMeter.zip',
    fileName: 'AlturionTapePowerMeter.zip'
  },
  'Fair Value Gap': {
    downloadLink: 'AlturionFairValueGap.zip',
    fileName: 'AlturionFairValueGap.zip'
  },
  'Cumulative Delta': {
    downloadLink: 'AlturionCumulativeDeltaPro.zip',
    fileName: 'AlturionCumulativeDeltaPro.zip'
  },
  'Delta Profile': {
    downloadLink: 'AlturionDeltaProfile.zip',
    fileName: 'AlturionDeltaProfile.zip'
  },
  'Big Trades': {
    downloadLink: 'AlturionBigTrades.zip',
    fileName: 'AlturionBigTrades.zip'
  }
};

/**
 * Récupère les informations de téléchargement pour un produit
 * @param {string} productName - Nom du produit
 * @returns {Object|null} Informations de téléchargement ou null si non trouvé
 */
export const getProductDownloadInfo = (productName) => {
  console.log('🔍 Recherche de téléchargement pour:', productName);
  console.log('📋 Mappings disponibles:', Object.keys(PRODUCT_DOWNLOADS));
  
  const result = PRODUCT_DOWNLOADS[productName] || null;
  
  if (result) {
    console.log('✅ Mapping trouvé:', result);
  } else {
    console.log('❌ Aucun mapping trouvé pour:', productName);
  }
  
  return result;
};

/**
 * Déclenche le téléchargement d'un produit via l'API sécurisée
 * @param {string} productName - Nom du produit à télécharger
 * @param {Function} onError - Callback en cas d'erreur
 * @returns {Promise<boolean>} - true si le téléchargement a été déclenché, false sinon
 */
export const downloadProduct = async (productName, onError = null) => {
  try {
    const downloadInfo = getProductDownloadInfo(productName);
    
    if (!downloadInfo) {
      const errorMsg = `Lien de téléchargement non disponible pour "${productName}"`;
      if (onError) onError(errorMsg);
      return false;
    }

    // Récupérer le token depuis le localStorage
    const userInfo = JSON.parse(localStorage.getItem('userInfo'));
    const token = userInfo ? userInfo.token : null;

    if (!token) {
      const errorMsg = 'Vous devez être connecté pour télécharger';
      if (onError) onError(errorMsg);
      return false;
    }

    devLog(`🚀 Début du téléchargement: ${downloadInfo.fileName}`);
    devLog(`🔑 Token utilisé: ${token.substring(0, 20)}...`);

    // ✅ Nouvelle approche : Obtenir l'URL présignée S3
    const apiUrl = getApiUrl(`/api/download/${downloadInfo.fileName}`);
    devLog(`🌐 URL de l'API: ${apiUrl}`);
    
    const response = await axios({
      url: apiUrl,
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`
      },
      timeout: 30000 // 30 secondes pour obtenir l'URL
    });

    if (!response.data.downloadUrl) {
      const errorMsg = 'URL de téléchargement non disponible';
      devError('❌ Pas d\'URL de téléchargement dans la réponse');
      if (onError) onError(errorMsg);
      return false;
    }

    devLog(`🔗 URL présignée obtenue: ${response.data.downloadUrl.substring(0, 50)}...`);

    // 👉 Nous laissons maintenant le navigateur télécharger directement le fichier.
    //    Cela évite les problèmes CORS avec S3.
    const directUrl = response.data.downloadUrl;
    devLog('⬇️ Lancement téléchargement via redirection navigateur');

    const a = document.createElement('a');
    a.href = directUrl;
    a.download = downloadInfo.fileName; // Fallback pour certains navigateurs
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Pas de vérification de taille possible ici, mais CORS est évité.
     
    devLog(`🎉 Téléchargement déclenché: ${downloadInfo.fileName}`);
    prodLog(`Download initiated: ${downloadInfo.fileName}`);
    return true;
    
  } catch (error) {
    devError('❌ Erreur lors du téléchargement:', sanitizeForLog(error));
    prodLog('Download error occurred', 'error');
    
    // ✅ Messages d'erreur plus détaillés
    let errorMsg = `Erreur lors du téléchargement de "${productName}"`;
    
    if (error.response) {
      if (error.response.status === 403) {
        errorMsg = 'Vous n\'avez pas les droits pour télécharger ce produit';
      } else if (error.response.status === 404) {
        errorMsg = 'Fichier non trouvé sur le serveur';
      } else if (error.response.status === 500) {
        errorMsg = 'Erreur serveur lors du téléchargement';
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMsg = 'Délai d\'attente dépassé lors du téléchargement';
    }
    
    if (onError) onError(errorMsg);
    return false;
  }
};

/**
 * Liste tous les produits disponibles au téléchargement
 * @returns {string[]} - Liste des noms de produits
 */
export const getAvailableProducts = () => {
  return Object.keys(PRODUCT_DOWNLOADS);
}; 